import type { Note, Order, Photo, Position, SyncEntity } from '@malerbetrieb/shared';
import Dexie, { type EntityTable } from 'dexie';

/**
 * Lokale Datenhaltung der PWA.
 *
 * IndexedDB ist die **einzige** Quelle für die Oberfläche. Es wird nie direkt
 * vom Server gelesen, um etwas anzuzeigen – dadurch verhält sich die App
 * online und offline identisch, und ein Netzausfall mitten in der Aufnahme
 * ändert am Bedienablauf nichts.
 */

/** Eintrag der Synchronisationswarteschlange. */
export interface OutboxEntry {
  /** Zugleich die Mutations-ID für die Idempotenz auf dem Server. */
  id: string;
  entity: SyncEntity;
  /** `upload` ist clientseitig: Foto-Binärdatei per Multipart. */
  op: 'upsert' | 'delete' | 'upload';
  recordId: string;
  /** Serverversion, auf der die Änderung basiert. 0 = Neuanlage. */
  baseVersion: number;
  payload: Record<string, unknown> | null;
  clientUpdatedAt: string;
  createdAt: string;
  attempts: number;
  lastError: string | null;
}

/** Lokal gespeicherte Bilddatei, bis sie hochgeladen ist. */
export interface PhotoBlobEntry {
  photoId: string;
  blob: Blob;
  /** Bleibt nach dem Upload als Offline-Vorschau erhalten. */
  uploaded: boolean;
}

/** Ein erkannter Konflikt – wird nie automatisch verworfen. */
export interface ConflictEntry {
  id: string;
  entity: SyncEntity;
  recordId: string;
  orderId: string | null;
  message: string;
  /** Der eigene Stand, der nicht übernommen wurde. */
  localRecord: Record<string, unknown> | null;
  /** Der Stand, der jetzt auf dem Server gilt. */
  serverRecord: Record<string, unknown> | null;
  createdAt: string;
  resolvedAt: string | null;
}

export interface MetaEntry {
  key: string;
  value: unknown;
}

/** Lokaler Datensatz = Serverfelder; `version` ist die zuletzt bestätigte Serverversion. */
export type LocalOrder = Order;
export type LocalPosition = Position;
export type LocalNote = Note;
export type LocalPhoto = Photo;

export class BaustellenDatenbank extends Dexie {
  orders!: EntityTable<LocalOrder, 'id'>;
  positions!: EntityTable<LocalPosition, 'id'>;
  notes!: EntityTable<LocalNote, 'id'>;
  photos!: EntityTable<LocalPhoto, 'id'>;

  outbox!: EntityTable<OutboxEntry, 'id'>;
  photoBlobs!: EntityTable<PhotoBlobEntry, 'photoId'>;
  conflicts!: EntityTable<ConflictEntry, 'id'>;
  meta!: EntityTable<MetaEntry, 'key'>;

  constructor() {
    super('malerbetrieb-baustellenaufnahme');

    this.version(1).stores({
      orders: 'id, status, updatedAt, deletedAt',
      areas: 'id, orderId, sortIndex, deletedAt',
      measurements: 'id, areaId, kind, sortIndex, deletedAt',
      deductions: 'id, measurementId, sortIndex, deletedAt',
      facadeMeasurements: 'id, areaId, sortIndex, deletedAt',
      checklistValues: 'id, areaId, [areaId+fieldKey], deletedAt',
      notes: 'id, orderId, areaId, deletedAt',
      photos: 'id, orderId, areaId, deletedAt',
      outbox: 'id, [entity+recordId], recordId, createdAt',
      photoBlobs: 'photoId, uploaded',
      conflicts: 'id, recordId, orderId, resolvedAt, createdAt',
      meta: 'key',
    });

    /**
     * Umbau auf Positionen (siehe UMBAU.md).
     *
     * Die Alt-Speicher werden mit `null` abgeräumt – ihr Inhalt ist auf dem
     * Server erhalten und wurde dort in Positionen überführt. Der Cursor wird
     * zurückgesetzt, damit der nächste Abgleich alles frisch lädt.
     */
    this.version(2)
      .stores({
        positions: 'id, orderId, sortIndex, deletedAt',
        photos: 'id, orderId, positionId, deletedAt',
        areas: null,
        measurements: null,
        deductions: null,
        facadeMeasurements: null,
        checklistValues: null,
      })
      .upgrade(async (tx) => {
        await tx.table('meta').put({ key: 'syncCursor', value: '0' });
      });
  }
}

export const db = new BaustellenDatenbank();

/** Schlüssel der `meta`-Tabelle. */
export const META_KEYS = {
  syncCursor: 'syncCursor',
  lastSyncAt: 'lastSyncAt',
  currentUserId: 'currentUserId',
} as const;

export async function readMeta<T>(key: string, fallback: T): Promise<T> {
  const entry = await db.meta.get(key);
  return entry === undefined ? fallback : (entry.value as T);
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/**
 * Fordert dauerhaften Speicher an, damit der Browser lokal erfasste
 * Baustellendaten nicht bei Speicherdruck verwirft.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;

  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}

export async function estimateStorage(): Promise<{ usage: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const estimate = await navigator.storage.estimate();
  return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
}

/**
 * Löscht alle lokalen Daten. Wird ausschließlich bei einem
 * Benutzerwechsel aufgerufen – nie automatisch beim Abmelden, weil dabei
 * noch nicht synchronisierte Aufnahmen verloren gingen.
 */
export async function clearLocalData(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.orders,
      db.positions,
      db.notes,
      db.photos,
      db.outbox,
      db.photoBlobs,
      db.conflicts,
      db.meta,
    ],
    async () => {
      await Promise.all([
        db.orders.clear(),
        db.positions.clear(),
        db.notes.clear(),
        db.photos.clear(),
        db.outbox.clear(),
        db.photoBlobs.clear(),
        db.conflicts.clear(),
        db.meta.clear(),
      ]);
    },
  );
}
